import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, CircularProgress, Link, Typography } from '@mui/material';
import { apiFetch } from '@diggens/api-client';
import { HubSpotLogo } from './HubSpotConnection';

type Outcome = 'finishing' | 'connected' | 'declined' | 'failed';

// Fixed text only: nothing HubSpot put in the URL is shown back.
const MESSAGES: Record<Exclude<Outcome, 'finishing'>, string> = {
  connected: 'HubSpot is connected. You can close this tab.',
  declined: 'HubSpot was not connected. You can close this tab and try again from Settings > Connections.',
  failed: 'HubSpot could not be connected. The sign-in may have expired or already been used. Try again from Settings > Connections.',
};

/**
 * Where HubSpot sends the browser back after the user approves (or declines) in the tab the Connections card opened. It
 * finishes the connect through the API under this tab's Diggens sign-in, so the connect completes only for the user
 * who started it.
 */
export function HubSpotCallback() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once, since the effect below takes them out of the URL
  const [{ code, state, declined }] = useState(() => ({ code: searchParams.get('code'), state: searchParams.get('state'), declined: searchParams.has('error') }));
  const [outcome, setOutcome] = useState<Outcome>(declined ? 'declined' : code && state ? 'finishing' : 'failed');
  // HubSpot's code works once, so it is posted once, even when StrictMode runs this effect twice.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    // Reloading the tab would otherwise post a used code and say the connect failed
    setSearchParams({}, { replace: true });
    if (declined || !code || !state) return;
    apiFetch<{ connected: boolean }>('/integrations/hubspot/callback', { method: 'POST', body: JSON.stringify({ code, state }) })
      .then(() => setOutcome('connected'))
      .catch((error: unknown) => {
        console.error('Could not finish the HubSpot connection:', error);
        setOutcome('failed');
      });
  }, [code, state, declined, setSearchParams]);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <HubSpotLogo />
            <Typography variant="h6">HubSpot</Typography>
          </Box>

          {outcome === 'finishing' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} aria-label="Connecting HubSpot" />
              <Typography color="text.secondary">Connecting HubSpot…</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>{MESSAGES[outcome]}</Typography>
              <Link component={RouterLink} to="/settings">
                Back to Settings
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
